import Session from "supertokens-web-js/recipe/session";
import { captureException } from "utils/errorReporting";
import { buildNativeRedirectUrl, clearNativeRedirect, getNativeHandoff, mintNativeHandoffCode } from "./nativeRedirect";

// The onboarded-shell middleware calls the handoff on every navigation; native only needs it once per
// session. `handedOff` guards the WebView postMessage; `redirecting` guards the system-browser redirect.
let handedOff = false;
let redirecting = false;

/**
 * Tell the RN WebView host the user is signed in and onboarded so native can adopt the session and close the WebView.
 */
export async function notifyNativeAuthSuccess() {
  const bridge = globalThis.window?.ReactNativeWebView;
  if (!bridge || handedOff) return;
  // Claim the slot before awaiting so overlapping navigations can't both post; release it if this attempt doesn't.
  handedOff = true;
  try {
    const userId = await Session.getUserId();
    if (!userId) {
      handedOff = false;
      return;
    }
    bridge.postMessage(JSON.stringify({ type: "qwick-auth-success", source: "marketplace", userId }));
  } catch (error) {
    handedOff = false;
    // Surface genuine handoff failures; the known WKWebView postMessage noise is filtered by Sentry's ignoredMessages.
    captureException(error);
  }
}

/** Hands the established session back to the native app by whichever channel this login is running through. */
export async function completeNativeHandoff() {
  const handoff = getNativeHandoff();
  if (!handoff) {
    await notifyNativeAuthSuccess();
    return;
  }
  // Claim the slot synchronously so overlapping shell navigations can't both mint a code.
  if (redirecting) return;
  redirecting = true;
  // Without the PKCE challenge or the server-issued launch nonce, fail closed: a link that carries neither
  // isn't a genuine app launch, and we won't mint a session-exchange code for it.
  if (!handoff.challenge || !handoff.launchNonce) {
    clearNativeRedirect();
    globalThis.location.assign(buildNativeRedirectUrl(handoff.redirect, { error: "invalid_request" }));
    return;
  }
  try {
    const code = await mintNativeHandoffCode(handoff.challenge, handoff.method ?? "S256", handoff.launchNonce);
    clearNativeRedirect();
    globalThis.location.assign(buildNativeRedirectUrl(handoff.redirect, { code }));
  } catch (error) {
    captureException(error);
    clearNativeRedirect();
    globalThis.location.assign(buildNativeRedirectUrl(handoff.redirect, { error: "handoff_failed" }));
  }
}

/** True while marketplace runs inside the native app's WebView, which injects the bridge; false in a plain browser. */
export function isNativeWebView() {
  return Boolean(globalThis.window?.ReactNativeWebView);
}

/** Re-arms both once-per-session handoffs; every fresh login calls it so a new session hands off again. */
export function resetNativeHandoff() {
  handedOff = false;
  redirecting = false;
}
